import { useEffect, useState } from "react";
import { ApiError, fetchForwardScorecard, type ScorecardBucketDto, type ScorecardResponseDto } from "../api";
import { ConvictionChip } from "../components/ConvictionBadge";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

// The analytic CI needs this many scored dates before _dm_test's HAC fit
// means anything (app/factory/scorecard.py's own floor) -- mirrored here
// only to phrase the "why is there no CI yet" message correctly, never to
// recompute anything client-side.
const MIN_DATES_FOR_CI = 20;

function fmtIc(v: number | null): string {
  if (v === null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(4)}`;
}

function fmtPct(v: number | null): string {
  if (v === null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function statusMeta(status: string): { label: string; cls: string; bots: string; botsCls: string } {
  if (status === "proven_live") return { label: "proven live", cls: "sc-flag-ok", bots: "eligible", botsCls: "sc-bots-on" };
  if (status === "underperforming") return { label: "underperforming", cls: "sc-flag-bad", bots: "excluded", botsCls: "sc-bots-off" };
  return { label: status, cls: "sc-flag-wait", bots: "locked", botsCls: "sc-bots-off" };
}

/** Fixed scale across every row so bars are comparable to each other, not
 * individually auto-scaled (which would make every row look equally
 * "big"). Domain padded a little past the widest real value seen. */
function makeDomain(buckets: ScorecardBucketDto[]): number {
  let m = 0.05;
  for (const b of buckets) {
    for (const v of [b.realized_ic_mean, b.expected_ic, b.ci_low, b.ci_high]) {
      if (v !== null && Number.isFinite(v)) m = Math.max(m, Math.abs(v));
    }
  }
  return m * 1.15;
}

function pct(v: number, domain: number): number {
  return ((v + domain) / (2 * domain)) * 100;
}

/** The realized-IC cell: a CI bar when the bucket has enough scored dates
 * for the analytic HAC estimator to mean anything, and an EXPLICIT "not
 * enough history yet" state otherwise -- never a fabricated narrow bar.
 * Every bucket in the live sample is currently in the second state; that
 * is the honest, intended first impression, not a bug. */
function IcCell({ bucket, domain }: { bucket: ScorecardBucketDto; domain: number }) {
  const hasCi = bucket.ci_low !== null && bucket.ci_high !== null;
  const zeroPos = pct(0, domain);
  return (
    <div className="sc-cibar-wrap">
      <div className="sc-cibar">
        <span className="sc-cibar-zero" style={{ left: `${zeroPos}%` }} />
        {hasCi ? (
          <span className="sc-cibar-rng" style={{ left: `${pct(bucket.ci_low!, domain)}%`, width: `${pct(bucket.ci_high!, domain) - pct(bucket.ci_low!, domain)}%` }} />
        ) : (
          <span className="sc-cibar-rng sc-cibar-nodata" title={`CI not yet computable -- ${bucket.n_dates_scored} scored date(s), needs >= ${MIN_DATES_FOR_CI}`} />
        )}
        {bucket.expected_ic !== null && (
          <span className="sc-cibar-exp" style={{ left: `${pct(bucket.expected_ic, domain)}%` }} title={`expected ${fmtIc(bucket.expected_ic)}`} />
        )}
        {bucket.realized_ic_mean !== null && (
          <span className="sc-cibar-pt" style={{ left: `${pct(bucket.realized_ic_mean, domain)}%` }} title={`realized ${fmtIc(bucket.realized_ic_mean)}`} />
        )}
      </div>
      <div className="sc-cibar-label">
        {!hasCi && <span className="note" style={{ fontSize: "0.68rem" }}>CI not yet computable (n={bucket.n_dates_scored})</span>}
      </div>
    </div>
  );
}

/** Hand-rolled inline SVG, not react-financial-charts -- the same
 * deviation ValuationBandChart.tsx already takes and for the same reason:
 * this is a much simpler shape (a handful of dots + one reference
 * diagonal, no zoom/pan/crosshair) and RFC has no primitive for it. */
function CalibrationPlot({ buckets }: { buckets: ScorecardBucketDto[] }) {
  const plottable = buckets.filter((b) => b.expected_ic !== null && b.realized_ic_mean !== null);
  if (plottable.length === 0) {
    return <div className="empty-state"><div className="headline">Nothing to calibrate yet</div><div>No bucket has both an expected and a realized IC yet.</div></div>;
  }
  const domain = makeDomain(plottable);
  const toXY = (expected: number, realized: number) => ({
    x: pct(expected, domain),
    y: 100 - pct(realized, domain),
  });
  const colorFor = (status: string) => (status === "proven_live" ? "var(--ok)" : status === "underperforming" ? "var(--bad)" : "var(--ink-faint)");

  return (
    <div className="sc-calib">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="sc-calib-svg">
        <line x1="0" y1="100" x2="100" y2="0" stroke="var(--rule-strong)" strokeWidth="0.5" strokeDasharray="2 2" />
        {plottable.map((b, i) => {
          const { x, y } = toXY(b.expected_ic!, b.realized_ic_mean!);
          return (
            <circle key={i} cx={x} cy={y} r="2.2" fill={colorFor(b.status)} stroke="var(--surface)" strokeWidth="0.6">
              <title>{`${b.segment_key} · ${b.conviction ?? "—"} · expected ${fmtIc(b.expected_ic)} vs realized ${fmtIc(b.realized_ic_mean)}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="sc-calib-leg">
        <div className="note" style={{ marginBottom: 6 }}>realized (vertical) vs expected (horizontal) -- below the dashed diagonal means the model promised more than the market delivered.</div>
        <div className="sc-calib-row"><i style={{ background: "var(--ok)" }} /> proven live</div>
        <div className="sc-calib-row"><i style={{ background: "var(--ink-faint)" }} /> accumulating</div>
        <div className="sc-calib-row"><i style={{ background: "var(--bad)" }} /> underperforming</div>
      </div>
    </div>
  );
}

export function ScorecardPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [data, setData] = useState<ScorecardResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { sorted, sortKey, sortDir, toggleSort } = useSort<ScorecardBucketDto>(
    data?.buckets ?? [],
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  useEffect(() => {
    setLoading(true);
    fetchForwardScorecard(token)
      .then(setData)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) onUnauthorized();
        else setError(err instanceof ApiError ? err.message : "request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!data) return <div className="page-sub">{loading ? "Loading…" : ""}</div>;

  const buckets = data.buckets;
  const domain = makeDomain(buckets);
  const nProven = buckets.filter((b) => b.status === "proven_live").length;
  const nAccumulating = buckets.filter((b) => b.status.startsWith("accumulating")).length;
  const nUnder = buckets.filter((b) => b.status === "underperforming").length;
  const totalFires = buckets.reduce((s, b) => s + b.n_fires, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Live Forward Scorecard</h1>
          <div className="page-sub">
            Expected vs realized, tracked forward from the day each signal fired. <FieldHelp term="forward_scorecard" /> By
            segment × conviction bucket.
          </div>
        </div>
      </div>

      <PageHelper>
        This is the ONE test nothing was fit to. Every row below is a segment × conviction bucket's real,
        already-fired predictions scored against what actually happened afterward. It is deliberately slow to fill
        in: a prediction only counts once it was genuinely made live (<FieldHelp term="scorecard_gate" />) and its
        horizon has fully closed. Most buckets reading "accumulating" is the design working correctly, not a thin
        or broken page.
      </PageHelper>

      <div className="sc-keyidea">
        <div className="sc-keyidea-dot">★</div>
        <div>
          <h4>Why this is the most convincing surface</h4>
          <p>
            Evaluation &amp; Champions and Model Builder are <b>historical</b> — however careful the walk-forward,
            the model has seen that data. This scorecard is <b>purely out-of-sample</b>: every row is realized
            performance <i>after</i> the signal fired, with zero lookahead and nothing fit to it. That is exactly
            why it is slow — and why a <FieldHelp term="proven_live_flag" /> <b>"proven live"</b> flag here means
            something a backtest never can.
          </p>
        </div>
      </div>

      {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

      <div className="kpis">
        <div className="kpi">
          <div className="l">Signals scored</div>
          <div className="v">{totalFires.toLocaleString()}</div>
          <div className="s">closed horizons only</div>
        </div>
        <div className="kpi">
          <div className="l">Buckets tracked</div>
          <div className="v">{buckets.length}</div>
          <div className="s">segment × conviction</div>
        </div>
        <div className="kpi">
          <div className="l">Proven live</div>
          <div className="v" style={{ color: "var(--ok)" }}>{nProven} <small style={{ fontSize: "0.9rem", color: "var(--ink-faint)" }}>/ {buckets.length}</small></div>
          <div className="s">cleared min-N + FDR</div>
        </div>
        <div className="kpi">
          <div className="l">Accumulating</div>
          <div className="v" style={{ color: "var(--ink-faint)" }}>{nAccumulating} <small style={{ fontSize: "0.9rem", color: "var(--ink-faint)" }}>/ {buckets.length}</small></div>
          <div className="s">flag still locked</div>
        </div>
        <div className="kpi">
          <div className="l">Underperforming</div>
          <div className="v" style={{ color: nUnder > 0 ? "var(--bad)" : "var(--ink-faint)" }}>{nUnder} <small style={{ fontSize: "0.9rem", color: "var(--ink-faint)" }}>/ {buckets.length}</small></div>
          <div className="s">a live miss, surfaced not hidden</div>
        </div>
      </div>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          <b>The gate:</b> {data.note}
        </span>
      </div>

      <section className="results-panel">
        <div className="results-bar">
          <div className="results-left">
            <span className="results-count"><b>{buckets.length}</b> buckets</span>
          </div>
          <div className="results-tools">
            <button
              className="btn ghost sm"
              disabled={buckets.length === 0}
              onClick={() =>
                exportRowsAsCsv(
                  sorted as unknown as Record<string, unknown>[],
                  [
                    { key: "segment_key", label: "Segment" }, { key: "conviction", label: "Conviction" },
                    { key: "horizon", label: "Horizon" }, { key: "realized_ic_mean", label: "Realized IC" },
                    { key: "ci_low", label: "CI low" }, { key: "ci_high", label: "CI high" },
                    { key: "expected_ic", label: "Expected IC" }, { key: "n_fires", label: "N fires" },
                    { key: "n_blocks", label: "N independent blocks" }, { key: "hit_rate", label: "Hit rate" },
                    { key: "net_of_benchmark", label: "Net vs NIFTY" }, { key: "status", label: "Status" },
                  ],
                  "forward_scorecard.csv",
                )
              }
            >
              ↓ CSV
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l clickable" onClick={() => toggleSort("segment_key")}>Segment {sortGlyph("segment_key", sortKey, sortDir)}</th>
                <th>Conviction <FieldHelp term="cross_horizon_conviction" /></th>
                <th className="r clickable" onClick={() => toggleSort("realized_ic_mean")}>
                  Realized IC <FieldHelp term="ic" /> · exp · CI <FieldHelp term="analytic_hac_ci" /> {sortGlyph("realized_ic_mean", sortKey, sortDir)}
                </th>
                <th className="r clickable" onClick={() => toggleSort("n_fires")}>IC (n) {sortGlyph("n_fires", sortKey, sortDir)}</th>
                <th className="r clickable" onClick={() => toggleSort("hit_rate")}>
                  Hit-rate <FieldHelp term="hit_rate" /> {sortGlyph("hit_rate", sortKey, sortDir)}
                </th>
                <th className="r clickable" onClick={() => toggleSort("net_of_benchmark")}>
                  Net vs bench <FieldHelp term="net_of_benchmark" /> {sortGlyph("net_of_benchmark", sortKey, sortDir)}
                </th>
                <th className="clickable" onClick={() => toggleSort("status")}>Status {sortGlyph("status", sortKey, sortDir)}</th>
                <th>Bots consult? <FieldHelp term="proven_live_flag" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((b, i) => {
                const meta = statusMeta(b.status);
                return (
                  <tr key={`${b.segment_key}-${b.conviction}-${b.horizon}-${i}`}>
                    <td className="l">
                      <span style={{ fontWeight: 600 }}>{b.segment_key}</span>
                      <div className="note mono" style={{ fontSize: "0.7rem" }}>h={b.horizon}d</div>
                    </td>
                    <td><ConvictionChip conviction={b.conviction} /></td>
                    <td className="r" style={{ minWidth: 170 }}><IcCell bucket={b} domain={domain} /></td>
                    <td className="r mono">
                      <span style={{ fontWeight: 600 }}>{fmtIc(b.realized_ic_mean)}</span>{" "}
                      <span className="note">({b.n_fires})</span>
                    </td>
                    <td className="r mono">{fmtPct(b.hit_rate)}</td>
                    <td className="r mono">{b.net_of_benchmark === null ? "—" : `${b.net_of_benchmark >= 0 ? "+" : ""}${(b.net_of_benchmark * 100).toFixed(2)}%`}</td>
                    <td>
                      <span className={`sc-flag ${meta.cls}`}>
                        <span className="sc-flag-dot" />{meta.label}
                      </span>
                    </td>
                    <td className={`mono sc-bots ${meta.botsCls}`} style={{ fontSize: "0.72rem" }}>
                      {meta.bots === "eligible" ? "● eligible" : meta.bots === "locked" ? "○ locked" : "○ excluded"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="foot-note">
          <b>CI method:</b> {data.ci_method} <b>Benchmark:</b> {data.benchmark} The "bots consult?" column shows what
          each bucket's flag WOULD permit today — the paper-trading bots this flag is meant to feed are a separate,
          not-yet-built module (V2 Track A); nothing is actually consulting it live yet.
        </div>
      </section>

      <div className="sec" style={{ marginTop: 28 }}>
        <div className="sec-h"><h2>Calibration — did reality match the model's confidence?</h2></div>
        <div className="sec-d">
          Each dot is a bucket with both an expected and a realized IC. On the diagonal = well-calibrated; below it
          = the model was overconfident. This is the honest mirror a backtest can never hold up to itself.
        </div>
        <div className="card">
          <CalibrationPlot buckets={buckets} />
        </div>
      </div>
    </>
  );
}
